/*
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
 */

import { groupsApi, peopleApi, searchApi } from "../AlfrescoApi.js";
import { newPristyError } from "./ErrorService.js";
import QuickLRU from "quick-lru";

/**
 * Service class for handling operations related to people, groups, and user search functionalities.
 */
class PeopleService {
  constructor() {
    this.lruGroupMemberships = new QuickLRU({
      maxSize: 20,
      maxAge: 30000, // 30 Sec
    });
    this.lruPeople = new QuickLRU({
      maxSize: 50,
      maxAge: 5 * 60 * 1000, // 5m
    });
  }

  getUser(userId) {
    const cacheKey = `user_${userId}`;
    if (this.lruPeople.has(cacheKey)) {
      return Promise.resolve(this.lruPeople.get(cacheKey));
    }

    const opts = {
      include: ["properties", "aspectNames", "capabilities"],
    };
    return peopleApi
      .getPerson(userId, opts)
      .then((user) => {
        this.lruPeople.set(cacheKey, user.entry);
        return user.entry;
      })
      .catch((err) => {
        throw newPristyError("getUser", err);
      });
  }

  getUserWithOpts(userId, opts) {
    const cacheKey = `user_with_opts_${userId}_${JSON.stringify(opts)}`;
    if (this.lruPeople.has(cacheKey)) {
      return Promise.resolve(this.lruPeople.get(cacheKey));
    }

    return peopleApi
      .getPerson(userId, opts)
      .then((response) => {
        this.lruPeople.set(cacheKey, response);
        return response;
      })
      .catch((err) => {
        throw newPristyError("getUser", err);
      });
  }

  updateUser(userId, personBodyUpdate) {
    return peopleApi
      .updatePerson(userId, personBodyUpdate)
      .then((response) => {
        // Invalidate cache for this user
        const cacheKey = `user_${userId}`;
        this.lruPeople.delete(cacheKey);
        // Also clear any cache entries with opts
        for (const key of this.lruPeople.keys()) {
          if (key.startsWith(`user_with_opts_${userId}_`)) {
            this.lruPeople.delete(key);
          }
        }
        return response.entry;
      })
      .catch((err) => {
        throw newPristyError("updateUser", err);
      });
  }

  listGroups(userId, refresh = false) {
    const cacheKey = `groups_${userId}`;
    if (!refresh && this.lruGroupMemberships.has(cacheKey)) {
      return Promise.resolve(this.lruGroupMemberships.get(cacheKey));
    }

    const opts = {};
    return groupsApi
      .listGroupMembershipsForPerson(userId, opts)
      .then((groups) => {
        this.lruGroupMemberships.set(cacheKey, groups.list.entries);
        return groups.list.entries;
      })
      .catch((error) => {
        throw newPristyError("listGroups", error);
      });
  }

  listUsers(refresh = false) {
    const cacheKey = `users`;
    if (!refresh && this.lruPeople.has(cacheKey)) {
      return Promise.resolve(this.lruPeople.get(cacheKey));
    }

    const opts = {
      include: ["properties", "aspectNames", "capabilities"],
    };
    return peopleApi
      .listPeople(opts)
      .then((response) => {
        const users = response.list.entries.map((data) => data.entry);
        this.lruPeople.set(cacheKey, users);
        return users;
      })
      .catch((err) => {
        throw newPristyError("getAllUsers", err);
      });
  }

  listUsersWithOpts(opts, refresh = false) {
    const cacheKey = `users_with_opts_${JSON.stringify(opts)}`;
    if (!refresh && this.lruPeople.has(cacheKey)) {
      return Promise.resolve(this.lruPeople.get(cacheKey));
    }

    /* const opts = {
      include: ["properties", "aspectNames", "capabilities"],
    }; */
    return peopleApi
      .listPeople(opts)
      .then((response) => {
        this.lruPeople.set(cacheKey, response);
        return response;
      })
      .catch((err) => {
        throw newPristyError("getAllUsers", err);
      });
  }

  capitalizeFirstLetter(string) {
    return string.charAt(0).toUpperCase() + string.slice(1);
  }

  searchUser(userInput, sort, opts = {}) {
    // Require at least 3 meaningful characters (ignoring @ and .)
    if (userInput.replace(/[@.]/g, "").length < 3) {
      return Promise.resolve({ list: { entries: [] } });
    }

    // Neutralise the only characters that would break an AFTS phrase.
    const escapeAfts = (value) => value.replace(/["\\]/g, "\\$&");

    // Name fields are searched on the "name" fragment: the local part when an
    // email is typed (before @), otherwise the whole input. The full input
    // (including @) is matched separately against cm:email.
    //
    // Note on cm:email and AFTS: the '@' is NOT a problem here. Solr keeps the
    // whole email domain as a single token (e.g. "pristy.net"), with '@' and
    // '-' as separators, so a wildcard phrase like cm:email:"*a@b.net*" matches
    // correctly and stays on the cached/indexed AFTS path.
    const nameFragment = userInput.includes("@")
      ? userInput.split("@")[0]
      : userInput;

    const clauses = [];

    // Only search name fields when the fragment is meaningful on its own, so an
    // input like "@domain.net" does not collapse the wildcard to a match-all.
    if (nameFragment.length >= 2) {
      // Case variants are kept because wildcard matching on these tokenised
      // fields is case-sensitive.
      const inputs = [
        nameFragment.toUpperCase(),
        nameFragment.toLowerCase(),
        this.capitalizeFirstLetter(nameFragment),
      ].map(escapeAfts);

      const nameFields = ["cm:userName", "cm:firstName", "cm:lastName"];
      for (const field of nameFields) {
        for (const value of inputs) {
          clauses.push(`${field}:"*${value}*"`);
        }
      }
    }

    // Email: match the full literal input (with @) as a single wildcard phrase.
    // Emails are stored lower-case.
    clauses.push(`cm:email:"*${escapeAfts(userInput.toLowerCase())}*"`);

    const aftsQuery = {
      // Variable/selective criteria in the main query.
      query: {
        language: "afts",
        query: `(${clauses.join(" OR ")})`,
      },
      // Structural criterion in filterQueries: Solr caches it as a reusable
      // bitset instead of recomputing it on every search.
      filterQueries: [{ query: 'TYPE:"cm:person"' }],
      // Force a fixed search locale, independent of the browser Accept-Language.
      // Under a fr_FR locale, AFTS wildcard search on the cm:person d:text
      // fields (firstName/lastName/email) returns nothing on some Solr setups,
      // whereas en_US works and folds accents (so "jer" matches "Jérémie").
      // User names/emails are not localized content, so a fixed locale is safe.
      localization: { locales: ["en_US"] },
      include: ["aspectNames", "properties"],
      paging: {
        maxItems: opts.maxItems || 1000,
        skipCount: opts.skipCount || 0,
      },
      sort: sort,
    };

    return searchApi.search(aftsQuery);
  }

  getUserGroups(userId, refresh = false) {
    const opts = { fields: ["id"] };

    if (!refresh && !this.lruGroupMemberships.has(userId)) {
      return groupsApi
        .listGroupMembershipsForPerson(userId, opts)
        .then((data) => {
          const groupIds = Object.values(data.list.entries).map(
            (entry) => entry.entry.id,
          );
          this.lruGroupMemberships.set(userId, groupIds);
          return groupIds;
        });
    }
    return Promise.resolve(this.lruGroupMemberships.get(userId));
  }
}

export default new PeopleService();
